import { formatCurrency } from "utils/currency";
import { orZero } from 'utils/NumUtils';

const getAverage = (array) => {
    if (array.length) {
        const total = array.reduce((acc, item) => {
            return parseFloat(acc) + parseFloat(item);
        }, 0)
        return total / (array.length === 1 ? 2 : array.length);
    } else {
        return array;
    }
}

const normalizeCoefficients = (arr: Array<number>): Array<number> => {
    const sum = arr.reduce((prev, curr) => prev + curr, 0);
    return arr.map(x => sum > 0 ? parseFloat((x / sum).toFixed(3)) : x);
}

export const getDataByMetric = (metric, d) => {
    switch (metric) {
        case 'Purchases':
            return d.last7Orders;
        case 'Revenue':
            return d.last7Revenue;
        case 'AdSpend':
            return d.last7Adspend;
        case 'ROAS':
            return d.last7Roas;
        case 'CPP':
            return d.last7Cpp;
        case 'AOV':
            return d.last7Aov;
        default:
            return d.last7Orders;
    }
};

export const formatValue = (metric, d) => {
    switch (metric) {
        case 'Purchases':
            return d.totals.orders;
        case 'Orders':
            return d.totals.orders;
        case 'Revenue':
            return formatCurrency(d.totals.revenue);
        case 'AdSpend':
            return formatCurrency(d.totals.spend);
        case 'ROAS':
            return d.totals.roas.toFixed(2);
        case 'CPP':
            return formatCurrency(d.totals.cpp);
        case 'AOV':
            return formatCurrency(d.totals.aov);
    }
}

export const getProbabilityPercentages = (metric, d) => {
    let above = 0;
    let below = 0;

    switch (metric) {

        case 'Purchases':
            const purchases = d.last7Orders;
            const purchaseAvg = getAverage(purchases)
            if (purchases.length) {
                for (let i = 0; i < 7; i++) {
                    if (purchases[i] !== undefined && purchases[i] !== 0 && purchases[i] >= purchaseAvg) {
                        above++
                    } else {
                        below++
                    }
                }
                return [above / 7, below / 7]
            }
            return [1, 0];

        case 'Revenue':
            const revenues = d.last7Revenue;
            const revenueAvg = getAverage(revenues)
            if (revenues.length) {
                for (let i = 0; i < 7; i++) {
                    if (revenues[i] !== undefined && revenues[i] !== 0 && revenues[i] >= revenueAvg) {
                        above++
                    } else {
                        below++
                    }
                }
                return [above / 7, below / 7]
            }
            return [1, 0];

        case 'AdSpend':
            const adSpends = d.last7Adspend;
            const adSpendsAvg = getAverage(adSpends)
            if (adSpends.length) {
                for (let i = 0; i < 7; i++) {
                    if (adSpends[i] !== undefined && adSpends[i] !== 0 && adSpends[i] >= adSpendsAvg) {
                        above++
                    } else {
                        below++
                    }
                }

                return [above / 7, below / 7]
            }
            return [1, 0];

        case 'ROAS':
            return [d.probIncRoas, d.probDecRoas];

        case 'CPP':
            const cpp = d.last7Cpp;
            const cppAvg = getAverage(cpp)
            if (cpp.length) {
                for (let i = 0; i < 7; i++) {
                    if (cpp[i] && cpp[i] >= cppAvg) {
                        above++
                    } else {
                        below++
                    }
                }

                return [above / 7, below / 7]
            }
            return [1, 0];

        case 'AOV':
            const aov = d.last7Aov;
            const aovAvg = getAverage(aov)
            if (aov.length) {
                for (let i = 0; i < 7; i++) {
                    if (aov[i] && aov[i] >= aovAvg) {
                        above++
                    } else {
                        below++
                    }
                }

                return [above / 7, below / 7]
            }
            return [1, 0];
    }
}

export const getProbabilities = (last7Roas) => {
    const baseCoefficients: Array<number> = [0.01, 0.02, 0.03, 0.20, 0.25, 0.40, 0.60];
    const coefficients = normalizeCoefficients(baseCoefficients);

    const last7RoasAvg = last7Roas.reduce((acc, item) => acc + item, 0) / last7Roas.length;
    const probabilities = last7Roas.map((roas, index) => [roas, coefficients[index]]);
    probabilities.sort((a, b) => a[0] - b[0]);

    // Accrue probabilities up to index
    const upToIndex = probabilities.findIndex(x => last7RoasAvg < x[0]);
    let probDec = 0.0;
    for (let i = 0; i < upToIndex; i++) {
        probDec += probabilities[i][1];
    }

    // Add interpolated probability
    if (upToIndex > 0) {
        let j = upToIndex - 1;
        probDec += ((last7RoasAvg - probabilities[j][0]) / (probabilities[j + 1][0] - probabilities[j][0])) * probabilities[j + 1][1];
    }

    let probInc = 1.0 - probDec;

    return [probDec, probInc];
}

export const last7Metrics = (data, c, metric) => {
    return Object.keys(data).map(key => {
        const day = data[key];
        const campaign = day.campaigns.filter(camp => camp.id === c.id && camp.name === c.name)[0];
        if (!campaign) return 0;
        switch (metric) {
            case 'Orders':
                return orZero(campaign.totals.orders);
            case 'Revenue':
                return orZero(campaign.totals.revenue);
            case 'AdSpend':
                return orZero(campaign.totals.adspend);
            case 'ROAS':
                return orZero(campaign.totals.roas);
            case 'CPP':
                return orZero(campaign.totals.cpp);
            case 'AOV':
                return orZero(campaign.totals.aov);
        }
    });
}

export const getRoasRecommendationCode = (campaign, roasGoal, probDecRoas, probIncRoas) => {
    let roasRecommendationCode = 0;
    if (campaign.totals.roas > roasGoal) {
        if ((campaign.totals.roas > roasGoal + 1.5) || probIncRoas > 0.50) {
            roasRecommendationCode = 1;
        }
    } else if (probDecRoas >= 0.80) {
        if (campaign.totals.roas < roasGoal) {
            roasRecommendationCode = -1;
        }
    } else if (probDecRoas > 0 && roasGoal - campaign.totals.roas >= 1.5) {
        roasRecommendationCode = -1;
    }

    return roasRecommendationCode;
}
